import React from 'react';
import { Box, Typography } from '@mui/material';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import {
  formatGuaranteesMoney,
  formatGuaranteesPeriod,
} from '~/modules/guarantees/form/client/formatters.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import type { TGuaranteesTenderDetails } from '~/modules/guarantees/form/client/types.ts';
import type { TGuaranteesProzorroTender } from '~/store/guarantees/types.ts';
import { formatGuaranteesSummaryValue } from '../../../utils/display/utils.ts';

interface ITenderSummaryProps {
  displayedTenderPublicId: string;
  prozorroTender: TGuaranteesProzorroTender | null;
  tenderDetails: TGuaranteesTenderDetails | null;
}

const TenderSummary: React.FC<ITenderSummaryProps> = ({
  displayedTenderPublicId,
  prozorroTender,
  tenderDetails,
}) => (
  <Box sx={guaranteesClientPageStyles.tenderSummaryPanel}>
    <Typography sx={guaranteesClientPageStyles.tenderSummaryTitle}>
      {tenderDetails?.summary.title || displayedTenderPublicId}
    </Typography>

    {!!tenderDetails?.summary.description && (
      <Typography sx={guaranteesClientPageStyles.flowOptionSource}>
        {tenderDetails.summary.description}
      </Typography>
    )}

    <Box sx={guaranteesClientPageStyles.tenderPrimaryValue}>
      <Typography sx={guaranteesClientPageStyles.detailLabel}>
        {guaranteesClientText.tenderValueLabel}
      </Typography>
      <Typography sx={guaranteesClientPageStyles.tenderPrimaryValueText}>
        {tenderDetails?.summary.expectedValue
          ? formatGuaranteesMoney(tenderDetails.summary.expectedValue)
          : guaranteesClientText.emptyValue}
      </Typography>
    </Box>

    <Box sx={guaranteesClientPageStyles.tenderSummaryMetaGrid}>
      <Box sx={guaranteesClientPageStyles.flowOptionMetaItem}>
        <Typography sx={guaranteesClientPageStyles.detailLabel}>
          {guaranteesClientText.tenderIdLabel}
        </Typography>
        <Typography sx={guaranteesClientPageStyles.detailValue}>
          {displayedTenderPublicId}
        </Typography>
      </Box>

      <Box sx={guaranteesClientPageStyles.flowOptionMetaItem}>
        <Typography sx={guaranteesClientPageStyles.detailLabel}>
          {guaranteesClientText.statusLabel}
        </Typography>
        <Typography sx={guaranteesClientPageStyles.detailValue}>
          {formatGuaranteesSummaryValue(prozorroTender?.status || tenderDetails?.summary.status)}
        </Typography>
      </Box>

      <Box sx={guaranteesClientPageStyles.flowOptionMetaItem}>
        <Typography sx={guaranteesClientPageStyles.detailLabel}>
          {guaranteesClientText.tenderPeriodLabel}
        </Typography>
        <Typography sx={guaranteesClientPageStyles.detailValue}>
          {formatGuaranteesPeriod(
            tenderDetails?.summary.tenderPeriodStart,
            tenderDetails?.summary.tenderPeriodEnd
          )}
        </Typography>
      </Box>

      <Box sx={guaranteesClientPageStyles.flowOptionMetaItem}>
        <Typography sx={guaranteesClientPageStyles.detailLabel}>
          {guaranteesClientText.enquiryPeriodLabel}
        </Typography>
        <Typography sx={guaranteesClientPageStyles.detailValue}>
          {formatGuaranteesPeriod(
            tenderDetails?.summary.enquiryPeriodStart,
            tenderDetails?.summary.enquiryPeriodEnd
          )}
        </Typography>
      </Box>

      <Box sx={guaranteesClientPageStyles.flowOptionMetaItem}>
        <Typography sx={guaranteesClientPageStyles.detailLabel}>
          {guaranteesClientText.tenderProcuringEntityLabel}
        </Typography>
        <Typography sx={guaranteesClientPageStyles.detailValue}>
          {formatGuaranteesSummaryValue(
            [tenderDetails?.summary.customerName, tenderDetails?.summary.customerCode]
              .filter(Boolean)
              .join(' / ')
          )}
        </Typography>
      </Box>

      <Box sx={guaranteesClientPageStyles.flowOptionMetaItem}>
        <Typography sx={guaranteesClientPageStyles.detailLabel}>
          {guaranteesClientText.ownerLabel}
        </Typography>
        <Typography sx={guaranteesClientPageStyles.detailValue}>
          {formatGuaranteesSummaryValue(tenderDetails?.summary.owner)}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default TenderSummary;
